import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {NameTemplatePartType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {getNameTemplatePartTypeToString, NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {isParentLinkSchemaField} from 'registry/child-entities/ChildEntityConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {
  BioentityLinkSchemaField,
  isBioentityLinkSchemaField,
  SchemaField,
} from 'schemas/fields/SchemaFieldConstants';
import {getNonArchivedOrComputedFields} from 'schemas/fields/SchemaFieldGetters';
import {OptionData} from 'ui-library/menus/MenuConstants';
import runModal from 'ui-library/runModal';
import * as Modals from 'util/Modals';

import NameTemplatePartConfiguration from './NameTemplatePartConfiguration';

function validSingleBioentityLinkFields(
  schemaFields: Array<SchemaField | ClientSchemaField>
): Array<BioentityLinkSchemaField> {
  return getNonArchivedOrComputedFields(schemaFields)
    .filter((field) => !field.isMulti)
    .filter((field): field is BioentityLinkSchemaField => isBioentityLinkSchemaField(field));
}

function validSingleParentLinkFields(
  schemaFields: Array<SchemaField | ClientSchemaField>
): Array<BioentityLinkSchemaField> {
  return BenchlingConfig.REGISTRY_ENABLE_CHILD_ENTITIES
    ? validSingleBioentityLinkFields(schemaFields).filter((field) => isParentLinkSchemaField(field))
    : [];
}

export function getOptionsForNameTemplatePartType(
  nameTemplatePartType: NameTemplatePartType,
  schemaFields: Array<SchemaField | ClientSchemaField>,
  appendPartFunc: (part: NameTemplatePart) => void,
  complexPolymerFields?: Array<SchemaField | ClientSchemaField>,
  nameTemplatePartConfiguration?: NameTemplatePartConfiguration
): Array<OptionData> {
  const name = getNameTemplatePartTypeToString(nameTemplatePartType, nameTemplatePartConfiguration);

  switch (nameTemplatePartType) {
    case NameTemplatePartType.ANTIBODY_DOMAIN:
      // TODO(BNCH-130157): We shouldn't need this function anymore.
      return [];
    case NameTemplatePartType.TEXT:
      return [
        {
          name,
          value: async () => {
            const text = await runModal(Modals.PromptModal, {
              enableRedesign: true,
              title: 'Enter a value',
              initialValue: '',
              validate: (s: string) => s.length > 0,
              medium: true,
            });
            if (text) {
              appendPartFunc({type: NameTemplatePartType.TEXT, text});
            }
          },
        },
      ];
    case NameTemplatePartType.SEPARATOR:
      return [
        {
          name,
          value: async () => {
            const text = await runModal(Modals.PromptModal, {
              enableRedesign: true,
              title: 'Enter a separator value',
              initialValue: '',
              validate: (s: string) => s.length > 0,
              medium: true,
            });
            if (text) {
              appendPartFunc({type: NameTemplatePartType.SEPARATOR, text});
            }
          },
        },
      ];
    case NameTemplatePartType.FIELD:
      return [
        ...getNonArchivedOrComputedFields(schemaFields).map((field: SchemaField) => ({
          name: `${field.name}`,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.FIELD, fieldId: field.apiId});
          },
        })),
      ];
    case NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER:
      return [
        ...validSingleBioentityLinkFields(schemaFields).map((field) => ({
          name: `${name}: ${field.name}`,
          value: () => {
            appendPartFunc({
              type: NameTemplatePartType.LINKED_BIOENTITY_REGISTRY_IDENTIFIER,
              fieldId: field.apiId,
            });
          },
        })),
      ];
    case NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER:
      return [
        ...validSingleParentLinkFields(schemaFields).map((field) => ({
          name: `${name}: ${field.name}`,
          value: () => {
            appendPartFunc({
              type: NameTemplatePartType.CHILD_ENTITY_LOT_NUMBER,
              fieldId: field.apiId,
            });
          },
        })),
      ];
    case NameTemplatePartType.CREATED_AT_YEAR:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.CREATED_AT_YEAR});
          },
        },
      ];
    case NameTemplatePartType.CREATED_AT_DATE:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.CREATED_AT_DATE});
          },
        },
      ];
    case NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER});
          },
        },
      ];
    case NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER_BASE_26:
      if (BenchlingConfig.ENABLE_BASE26_ID_NAME_TEMPLATE_PART) {
        return [
          {
            name,
            value: () => {
              appendPartFunc({type: NameTemplatePartType.REGISTRY_IDENTIFIER_NUMBER_BASE_26});
            },
          },
        ];
      }
      return [];
    case NameTemplatePartType.BARCODE:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.BARCODE});
          },
        },
      ];
    case NameTemplatePartType.BARCODE_NUMBER:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.BARCODE_NUMBER});
          },
        },
      ];
    case NameTemplatePartType.CONTAINER_CREATED_AT_DATE:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.CONTAINER_CREATED_AT_DATE});
          },
        },
      ];
    case NameTemplatePartType.CONTAINER_CREATED_AT_YEAR:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.CONTAINER_CREATED_AT_YEAR});
          },
        },
      ];
    case NameTemplatePartType.FILL_DATE:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.FILL_DATE});
          },
        },
      ];
    case NameTemplatePartType.FILL_YEAR:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.FILL_YEAR});
          },
        },
      ];
    case NameTemplatePartType.PARENT_STORABLE_BARCODE:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.PARENT_STORABLE_BARCODE});
          },
        },
      ];
    case NameTemplatePartType.PARENT_STORABLE_NAME:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.PARENT_STORABLE_NAME});
          },
        },
      ];
    case NameTemplatePartType.POSITION:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.POSITION});
          },
        },
      ];
    case NameTemplatePartType.PROJECT:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.PROJECT});
          },
        },
      ];
    case NameTemplatePartType.QUANTITY_VALUE:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.QUANTITY_VALUE});
          },
        },
      ];
    case NameTemplatePartType.QUANTITY_UNITS:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.QUANTITY_UNITS});
          },
        },
      ];
    case NameTemplatePartType.RESTRICTION_STATUS:
      return hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)
        ? [
            {
              name,
              value: () => {
                appendPartFunc({type: NameTemplatePartType.RESTRICTION_STATUS});
              },
            },
          ]
        : [];
    case NameTemplatePartType.SAMPLE_LOT_NUMBER:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.SAMPLE_LOT_NUMBER});
          },
        },
      ];
    case NameTemplatePartType.CONCENTRATION_VALUE:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.CONCENTRATION_VALUE});
          },
        },
      ];
    case NameTemplatePartType.CONCENTRATION_UNITS:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.CONCENTRATION_UNITS});
          },
        },
      ];
    case NameTemplatePartType.CONTAINED_ENTITY_NAME:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.CONTAINED_ENTITY_NAME});
          },
        },
      ];
    case NameTemplatePartType.COMPLEX_POLYMER_COMPONENT:
      return (complexPolymerFields ?? []).map((field) => ({
        name: `${name}: ${field.name}`,
        value: () => {
          appendPartFunc({type: NameTemplatePartType.COMPLEX_POLYMER_COMPONENT, fieldId: field.apiId ?? ''});
        },
      }));
    case NameTemplatePartType.PROCEDURE_NAME:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.PROCEDURE_NAME});
          },
        },
      ];
    case NameTemplatePartType.PROCEDURE_RUN_ID:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.PROCEDURE_RUN_ID});
          },
        },
      ];
    case NameTemplatePartType.STUDY_ID:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.STUDY_ID});
          },
        },
      ];
    case NameTemplatePartType.STUDY_NAME:
      return [
        {
          name,
          value: () => {
            appendPartFunc({type: NameTemplatePartType.STUDY_NAME});
          },
        },
      ];
    case NameTemplatePartType.HOISTED_FIELD:
      // TODO: remove from name template part types -- no longer supported
      return [];
    default:
      assertAndContinue(false, `Expected supported name template type, got ${nameTemplatePartType}`);
      return [];
  }
}
